import { getScrollEventTarget, getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';

/**
 * Parallax scene — a stack of background images that dissolve between each other as
 * their scenes scroll past, with a staggered content reveal per scene.
 *
 * Two dissolve timings, chosen per section:
 *
 * TRIGGERED (default, and how the prototype works). An observer whose root is
 * collapsed to the middle of the screen reports which scene is currently crossing
 * that line; the layer for that scene gets `.is-visible` and CSS runs a fixed
 * transition. The animation owns its own clock, so stopping mid-scroll can never
 * strand a dissolve half way. The outgoing layer's transition-delay is what makes
 * the two overlap — see the stylesheet.
 *
 * SCROLL-LINKED. Opacity is written per frame from the continuous scene position
 * `u`: 0 when scene 0 is centred, 1 when scene 1 is, negative while approaching.
 *
 *   layer i opacity   rises 0 -> 1 as u goes from (i - w) to i
 *   the stack         fades out as u passes (n - 1)
 *
 * Layer 0's rise is the entry fade, the middle layers are the scene changes, and
 * the stack's fall is the exit fade — three behaviours from one formula.
 *
 * Pinning is CSS (position: sticky) in both modes, and the layers default to
 * opaque, so the images survive this file failing to load.
 *
 * Scroll plumbing: above 990px Horizon makes `.page-wrapper` the scroll container
 * rather than the document, so `window` emits no scroll events and observers need
 * an explicit root. Both come from @theme/scroll-container and are rebuilt when
 * that breakpoint is crossed.
 */

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const mobileQuery = window.matchMedia('(max-width: 749px)');

const REVEAL_DELAYS = [0.05, 0.4, 0.75, 1.1];
const REVEAL_STEP = 0.35;
const NESTED_DELAY = 1.7;
/** Drift at full intensity, as a fraction of viewport height. */
const MAX_TRAVEL = 0.3;

const mounted = new Set();
const animating = new Set();

let frameRequested = false;

function requestUpdate() {
  if (frameRequested) return;
  frameRequested = true;
  requestAnimationFrame(() => {
    frameRequested = false;
    for (const section of animating) section.update();
  });
}

const clamp = (/** @type {number} */ value, /** @type {number} */ min, /** @type {number} */ max) =>
  Math.min(Math.max(value, min), max);
/** Cubic. A photograph is legible at 30% opacity, so a linear entry reads as
 *  "already visible" long before it is. Used where absence matters. */
const easeIn = (/** @type {number} */ t) => t * t * t;
/** Smoothstep. Used between scenes, where both images are present and the only
 *  goal is a handover without a visible seam. */
const smooth = (/** @type {number} */ t) => t * t * (3 - 2 * t);

let motionObserver = /** @type {IntersectionObserver | null} */ (null);
let revealObserver = /** @type {IntersectionObserver | null} */ (null);
let sceneObserver = /** @type {IntersectionObserver | null} */ (null);
let scrollTarget = /** @type {EventTarget | null} */ (null);

function buildObservers() {
  const root = getIntersectionRoot();

  motionObserver?.disconnect();
  motionObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const section = entry.target;
        if (!(section instanceof ParallaxScene)) continue;
        if (entry.isIntersecting) animating.add(section);
        else animating.delete(section);
      }
      requestUpdate();
    },
    // Generous margin: with bleed the dissolve starts before the section itself
    // reaches the viewport, so we need to be updating by then.
    { root, rootMargin: '100% 0px 100% 0px', threshold: 0 }
  );

  revealObserver?.disconnect();
  revealObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) entry.target.classList.add('is-playing');
      }
    },
    { root, rootMargin: '0px 0px -15% 0px', threshold: 0 }
  );

  // Collapsing the root to a zero-height band at the middle of the screen means
  // exactly one scene panel intersects at a time — that scene is the active one.
  sceneObserver?.disconnect();
  sceneObserver = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const host = entry.target.closest('parallax-scene');
        if (!(host instanceof ParallaxScene)) continue;
        host.setPanelActive(/** @type {HTMLElement} */ (entry.target), entry.isIntersecting);
      }
    },
    { root, rootMargin: '-50% 0px -50% 0px', threshold: 0 }
  );

  for (const section of mounted) section.observe();
}

function bindScroll() {
  scrollTarget?.removeEventListener('scroll', requestUpdate);
  scrollTarget = getScrollEventTarget();
  scrollTarget.addEventListener('scroll', requestUpdate, { passive: true });
}

buildObservers();
bindScroll();

window.addEventListener('resize', requestUpdate, { passive: true });
// Belt and braces: whichever element actually scrolls, one of these fires.
window.addEventListener('scroll', requestUpdate, { passive: true, capture: true });

scrollContainerMediaQuery.addEventListener('change', () => {
  buildObservers();
  bindScroll();
  requestUpdate();
});

class ParallaxScene extends HTMLElement {
  #layers = /** @type {HTMLElement[]} */ ([]);
  #panels = /** @type {HTMLElement[]} */ ([]);
  #columns = /** @type {HTMLElement[]} */ ([]);
  #intersecting = /** @type {Set<HTMLElement>} */ (new Set());
  #active = -1;

  connectedCallback() {
    this.#layers = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-parallax-layer]')));
    this.#panels = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.parallax-scene__scene')));
    this.#columns = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-parallax-content]')));

    if (this.animatesContent) {
      for (const column of this.#columns) this.#assignRevealDelays(column);
    } else {
      for (const column of this.#columns) column.classList.add('is-playing');
    }

    mounted.add(this);
    this.observe();
    this.#mountSlides();

    // Settle the correct state synchronously, before the transitions are armed
    // below, so nothing animates from a wrong starting value on load.
    if (this.triggered) this.#applyActive(this.#measureActive());
    this.update();
    requestAnimationFrame(() => this.classList.add('parallax-scene--ready'));

    this.addEventListener('shopify:block:select', (event) => {
      const target = /** @type {Element | null} */ (event.target);
      target?.closest('[data-parallax-content]')?.classList.add('is-playing');
    });
  }

  disconnectedCallback() {
    motionObserver?.unobserve(this);
    for (const column of this.#columns) revealObserver?.unobserve(column);
    for (const panel of this.#panels) sceneObserver?.unobserve(panel);
    mounted.delete(this);
    animating.delete(this);
  }

  observe() {
    motionObserver?.observe(this);
    for (const panel of this.#panels) sceneObserver?.observe(panel);
    if (!this.animatesContent) return;
    for (const column of this.#columns) revealObserver?.observe(column);
  }

  get animatesContent() {
    return this.dataset.animateContent === 'true';
  }

  get triggered() {
    return this.dataset.timing !== 'scroll';
  }

  get intensity() {
    return clamp(Number.parseFloat(this.dataset.intensity ?? '40') / 100, 0, 1);
  }

  /** Dissolve length, in scene units. Scroll-linked timing only. */
  get dissolve() {
    return clamp(Number.parseFloat(this.dataset.fadeDistance ?? '70') / 100, 0.1, 1);
  }

  get fadeEnabled() {
    return this.dataset.fadeIn === 'true';
  }

  get driftEnabled() {
    if (this.dataset.motion !== 'offset') return false;
    if (reducedMotion.matches) return false;
    if (this.dataset.disableMotionMobile === 'true' && mobileQuery.matches) return false;
    return true;
  }

  /**
   * Called by the midline observer as panels cross it.
   * @param {HTMLElement} panel
   * @param {boolean} isIntersecting
   */
  setPanelActive(panel, isIntersecting) {
    if (isIntersecting) this.#intersecting.add(panel);
    else this.#intersecting.delete(panel);

    if (!this.triggered || !this.fadeEnabled) return;

    // Normally exactly one panel straddles the midline. If a gap or a rounding
    // edge leaves two, the earlier one wins so the sequence never runs backwards.
    let index = -1;
    for (const candidate of this.#intersecting) {
      const found = this.#panels.indexOf(candidate);
      if (found !== -1 && (index === -1 || found < index)) index = found;
    }
    this.#applyActive(index);
  }

  /** Which panel straddles the midline right now, without waiting on the observer. */
  #measureActive() {
    const middle = window.innerHeight / 2;
    for (let index = 0; index < this.#panels.length; index += 1) {
      const panel = this.#panels[index];
      if (!panel) continue;
      const rect = panel.getBoundingClientRect();
      if (rect.top <= middle && rect.bottom >= middle) return index;
    }
    return -1;
  }

  /** @param {number} index -1 means no scene is active, so every layer fades out. */
  #applyActive(index) {
    if (index === this.#active) return;
    this.#active = index;
    this.#layers.forEach((layer, position) => {
      layer.classList.toggle('is-visible', position === index);
    });
  }

  /** Wires up any scene that has carousel images (see blocks/_scene.liquid). */
  #mountSlides() {
    for (const nav of this.querySelectorAll('[data-parallax-slidenav]')) {
      const panel = /** @type {HTMLElement | null} */ (nav.closest('.parallax-scene__scene'));
      const id = panel?.dataset.sceneId;
      const layer = /** @type {HTMLElement | null} */ (
        id ? this.querySelector(`[data-parallax-layer][data-scene-id="${id}"]`) : null
      );
      if (!panel || !layer) continue;
      new SceneSlides(layer, panel, /** @type {HTMLElement} */ (nav));
    }
  }

  /** @param {HTMLElement} column */
  #assignRevealDelays(column) {
    const blocks = /** @type {HTMLElement[]} */ (Array.from(column.children));

    blocks.forEach((block, index) => {
      const delay =
        index < REVEAL_DELAYS.length
          ? REVEAL_DELAYS[index]
          : REVEAL_DELAYS[REVEAL_DELAYS.length - 1] + (index - REVEAL_DELAYS.length + 1) * REVEAL_STEP;

      block.classList.add('parallax-reveal');
      block.style.setProperty('--parallax-delay', `${delay}s`);
    });

    // The final block's own line lands with the stack; its supporting children
    // follow later, mirroring the prototype's note-and-buttons beat.
    // Index access rather than .at(-1): the theme's jsconfig targets ES2020.
    const last = blocks[blocks.length - 1];
    if (!last || last.children.length < 2) return;

    for (const child of /** @type {HTMLElement[]} */ (Array.from(last.children).slice(1))) {
      child.classList.add('parallax-reveal');
      child.style.setProperty('--parallax-delay', `${NESTED_DELAY}s`);
    }
  }

  update() {
    const count = this.#layers.length;
    if (count === 0) return;

    const rect = this.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Drift rides a custom property so it composes with the pin's own transform
    // rather than fighting it.
    if (this.driftEnabled) {
      const travel = viewportHeight * MAX_TRAVEL * this.intensity;
      const centreDelta = rect.top + rect.height / 2 - viewportHeight / 2;
      const progress = clamp(-centreDelta / ((viewportHeight + rect.height) / 2), -1, 1);
      this.style.setProperty('--parallax-drift', `${((progress * travel) / 2).toFixed(2)}px`);
    } else {
      this.style.removeProperty('--parallax-drift');
    }

    // Triggered timing owns opacity through CSS classes — never write an inline
    // value here, it would outrank the stylesheet and freeze the dissolve.
    if (this.triggered || !this.fadeEnabled) {
      for (const layer of this.#layers) layer.style.opacity = '';
      return;
    }

    const w = this.dissolve;
    const sceneHeight = rect.height / count;
    // Continuous scene position: 0 when scene 0 is centred, 1 when scene 1 is.
    const u = (viewportHeight / 2 - rect.top - sceneHeight / 2) / sceneHeight;
    // The whole stack retires together once the last scene has been centred.
    const exit = 1 - easeIn(clamp((u - (count - 1)) / w, 0, 1));

    this.#layers.forEach((layer, index) => {
      const t = clamp((u - (index - w)) / w, 0, 1);
      // The first layer's rise is the entry fade — ease it so the image stays
      // genuinely absent. Later layers hand over between two visible images.
      const base = index === 0 ? easeIn(t) : smooth(t);
      layer.style.opacity = (base * exit).toFixed(3);
    });
  }
}

/** Minimum horizontal travel, in px, for a pointer gesture to count as a swipe. */
const SWIPE_DISTANCE = 40;

/**
 * One scene's image carousel. The scene's main image is slide 0 and always
 * paints; the extra slides are overlays inside the pinned layer that cross-fade
 * on top of it. Controls live in the scene's content panel (arrows, dots), with
 * swipe and arrow keys on the panel too. Scrolling is untouched: the layer still
 * dissolves into the next scene exactly as before, whichever slide is showing.
 */
class SceneSlides {
  /**
   * @param {HTMLElement} layer
   * @param {HTMLElement} panel
   * @param {HTMLElement} nav
   */
  constructor(layer, panel, nav) {
    this.layer = layer;
    this.panel = panel;
    this.slides = /** @type {HTMLElement[]} */ (Array.from(layer.querySelectorAll('[data-parallax-slide]')));
    this.videos = Array.from(layer.querySelectorAll('video'));
    this.dots = /** @type {HTMLElement[]} */ (Array.from(nav.querySelectorAll('[data-parallax-dot]')));
    this.live = nav.querySelector('[data-parallax-slide-live]');
    this.count = this.slides.length + 1;
    this.index = 0;
    this.timer = 0;
    this.paused = false;
    this.pointerStart = /** @type {{ x: number, y: number } | null} */ (null);
    const seconds = Number.parseFloat(nav.dataset.interval ?? '6');
    this.intervalMs = nav.dataset.autoplay === 'true' && !reducedMotion.matches ? clamp(seconds, 2, 30) * 1000 : 0;

    nav.querySelector('[data-parallax-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    nav.querySelector('[data-parallax-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

    panel.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.index - 1, true);
      else if (event.key === 'ArrowRight') this.go(this.index + 1, true);
      else return;
      event.preventDefault();
    });

    // Swipe on the panel, which covers the image. A drag that starts on a link
    // or button is left alone, and the vertical check keeps an ordinary page
    // scroll from flipping the image.
    panel.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        const target = /** @type {Element | null} */ (event.target);
        if (target?.closest('a, button, input, select, textarea')) return;
        this.pointerStart = { x: event.clientX, y: event.clientY };
      },
      { passive: true }
    );
    panel.addEventListener(
      'pointerup',
      (event) => {
        const start = this.pointerStart;
        this.pointerStart = null;
        if (!start) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) < SWIPE_DISTANCE || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        this.go(this.index + (dx < 0 ? 1 : -1), true);
      },
      { passive: true }
    );
    panel.addEventListener('pointercancel', () => (this.pointerStart = null), { passive: true });

    // Only a mouse hovering pauses the clock. On touch, pointerenter fires on
    // every tap and the button keeps focus afterwards, which would stall the
    // carousel for good after one swipe.
    panel.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.pause(true);
    });
    panel.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.pause(false);
    });
    panel.addEventListener('focusin', (event) => {
      const target = /** @type {Element | null} */ (event.target);
      if (target?.matches(':focus-visible')) this.pause(true);
    });
    panel.addEventListener('focusout', () => this.pause(false));
    document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));

    this.preload(1);
    this.start();
  }

  /**
   * The extra images load lazily so the page is not paying for six hero-sized
   * files up front; this fetches just the one that is about to be needed.
   * @param {number} index
   */
  preload(index) {
    const slide = this.slides[((index % this.count) + this.count) % this.count - 1];
    const img = slide?.querySelector('img');
    if (img && img.loading === 'lazy') img.loading = 'eager';
  }

  /**
   * @param {number} index wraps at either end
   * @param {boolean} [byUser] restarts the autoplay clock so a click is not undone by a pending tick
   */
  go(index, byUser = false) {
    const next = ((index % this.count) + this.count) % this.count;
    if (next === this.index) return;
    this.index = next;
    this.slides.forEach((slide, i) => slide.classList.toggle('is-current', i === next - 1));
    this.preload(next + 1);
    // The main image may carry a video; it only makes sense while slide 0 shows.
    for (const video of this.videos) {
      if (next === 0) video.play().catch(() => {});
      else video.pause();
    }
    this.dots.forEach((dot, i) => dot.setAttribute('aria-selected', i === next ? 'true' : 'false'));
    if (this.live) this.live.textContent = `Image ${next + 1} of ${this.count}`;
    if (byUser) {
      // A swipe, tap or click restarts the clock so the next automatic flip is
      // a full interval away.
      this.stop();
      this.start();
    }
  }

  /** @param {boolean} paused */
  pause(paused) {
    this.paused = paused;
    if (paused) this.stop();
    else this.start();
  }

  start() {
    if (!this.intervalMs || this.paused || this.timer) return;
    this.timer = window.setInterval(() => {
      // Only flip while this scene's image is actually the one on screen.
      const shown = this.layer.classList.contains('is-visible') || Number.parseFloat(getComputedStyle(this.layer).opacity) >= 0.5;
      if (!shown) return;
      this.go(this.index + 1);
    }, this.intervalMs);
  }

  stop() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = 0;
  }
}

if (!customElements.get('parallax-scene')) {
  customElements.define('parallax-scene', ParallaxScene);
}
